import React, { useState } from 'react';
import axios from 'axios';
import endpoints from '../../define/endpoints';
import ModalDrawer from './ModalDrawer';
import PropTypes from 'prop-types';
import Button, { ButtonSizes, ButtonStyles } from '../Button/Button';
import BetaIcon, { IconColors, IconSizes } from '../Icons/Icon';
import { useMatchesMinWidth } from '../../hooks/useMatchesMinWidth';
import { TRANSLATIONS } from '../../../translations';
import { BREAKPOINTS } from '../../../constants';
import { Loader } from '../common';
import Typography, {
  TypographyVariants,
  TypographyWeights,
} from '../../ui-components/Typography/Typography';

const SUCCESS_ICON = 'circleCheck';
const FAIL_ICON = 'warning';
const INFO = 'info';

const ModalResendEmail = ({ order, isOpen, setIsOpen, token, userEmail }) => {
  const isDeviceSmallerThanMblLandscape = !useMatchesMinWidth(
    BREAKPOINTS.MBL_LANDSCAPE
  );
  const [showResponse, setShowResponse] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showRetry, setShowRetry] = useState(true);
  const [responseContent, setResponseContent] = useState('');

  const resendEmail = async (order) => {
    const orderId = order.id;
    setIsLoading(true);
    setShowResponse(true);
    try {
      await axios.post(
        `${endpoints.core}/orders/${orderId}/resend-confirm-email`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const successMessage =
        TRANSLATIONS.CONFIRM_EMAIL_SENT +
        '<br>' +
        TRANSLATIONS.CANT_FIND_EMAIL +
        ' <b>' +
        TRANSLATIONS.IN_SPAM +
        '</b> ' +
        TRANSLATIONS.CHECK_YOUR_EMAIL;
      setIsSuccess(true);
      setResponseContent(successMessage);
    } catch (error) {
      setIsSuccess(false);
      const errorMessage =
        error.response?.data?.errors ||
        TRANSLATIONS.EMAIL_RESEND_FAILED_MESSAGE;

      if (errorMessage === 'You have already request to resend the email') {
        setShowRetry(false);
        setResponseContent(`${TRANSLATIONS.YOU_HAVE_ALREADY_REQUEST_TO_RESEND_THE_EMAIL} <br/> 
          ${TRANSLATIONS.CANT_FIND_EMAIL} <b>${TRANSLATIONS.IN_SPAM}</b> ${TRANSLATIONS.CHECK_YOUR_EMAIL}`);
      } else {
        setResponseContent(TRANSLATIONS.EMAIL_RESEND_FAILED_MESSAGE);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!order) return <></>;
  const responseModalDrawer = (
    <ModalDrawer
      isOpen={showResponse}
      setIsOpen={setShowResponse}
      isDeviceSmallerThanTablet={isDeviceSmallerThanMblLandscape}
      modalCustomWidth={640}
      title={TRANSLATIONS.RESEND_PURCHASE_EMAIL}
      content={
        <div className="pl-m pr-m pt-xl pb-xl w-100%">
          {isLoading ? (
            <div className="d-flex justify-center text-center">
              <Loader loading={isLoading} />
            </div>
          ) : (
            <div className="d-flex flex-column align-center items-center gap-m text-center pb-2xs gap-xs">
              <BetaIcon
                name={isSuccess ? SUCCESS_ICON : FAIL_ICON}
                color={IconColors.regular}
                size={IconSizes[64]}
              />

              {isSuccess && (
                <Typography
                  variant={TypographyVariants.p2}
                  weight={TypographyWeights.bold}
                >
                  {TRANSLATIONS.EMAIL_SENT_SUCCESSFULLY}
                </Typography>
              )}

              <div
                className="typography__p2 typography__neutral text-center"
                dangerouslySetInnerHTML={{ __html: responseContent }}
              />

              {!isSuccess && showRetry && (
                <Button
                  size={ButtonSizes.xs}
                  style={ButtonStyles.textUnderlinedAlways}
                  label={<b>{TRANSLATIONS.RETRY_NOW}</b>}
                  onClick={() => resendEmail(order)}
                />
              )}
            </div>
          )}
        </div>
      }
      footer={
        <div className="d-flex justify-center gap-m pb-m pl-xl pr-xl">
          <Button
            classNames={'pr-m pl-m'}
            size={ButtonSizes.s}
            style={ButtonStyles.empty}
            label={<b>{TRANSLATIONS.CLOSE}</b>}
            onClick={() => setShowResponse(false)}
          />
        </div>
      }
    />
  );

  return (
    <>
      {responseModalDrawer}
      <ModalDrawer
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        isDeviceSmallerThanTablet={isDeviceSmallerThanMblLandscape}
        modalCustomWidth={640}
        title={TRANSLATIONS.RESEND_PURCHASE_EMAIL}
        content={
          <div className="pl-m pr-m pt-xl pb-3xl w-100% d-flex flex-column gap-m">
            <div className="w-100% d-flex justify-center">
              <BetaIcon
                name={INFO}
                color={IconColors.regular}
                size={IconSizes[64]}
              />
            </div>

            <div>
              <Typography
                classNames="text-center"
                variant={TypographyVariants.p2}
                weight={TypographyWeights.bold}
              >
                {
                  TRANSLATIONS.YOU_ARE_ABOUT_TO_REQUEST_THE_PURCHASE_CONFIRMATION_EMAIL
                }
              </Typography>
              <Typography
                classNames="text-center"
                variant={TypographyVariants.p2}
              >
                {TRANSLATIONS.EMAIL_WILL_BE_SENT_TO_THE_ADDRESS}{' '}
                <b>{userEmail}</b>
              </Typography>
            </div>
            <Typography
              classNames="text-center"
              variant={TypographyVariants.p3}
            >
              {TRANSLATIONS.IF_THE_EMAIL_IS_WRONG}{' '}
              <a href={window.freedomeConfig.knownPages.profile}>
                {TRANSLATIONS.DASHBOARD.toLowerCase()}
              </a>
            </Typography>
          </div>
        }
        footer={
          <div className="d-flex gap-m pb-m pl-xl pr-xl">
            <Button
              classNames={'flex-1 pr-m pl-m'}
              size={ButtonSizes.s}
              style={ButtonStyles.empty}
              label={TRANSLATIONS.CANCEL}
              onClick={() => setIsOpen(false)}
            />
            <Button
              classNames={'flex-1 pr-m pl-m'}
              size={ButtonSizes.s}
              style={ButtonStyles.secondary}
              label={'Invia email'}
              onClick={() => {
                setIsOpen(false), resendEmail(order);
              }}
              disabled={isLoading}
            />
          </div>
        }
      />
    </>
  );
};

ModalResendEmail.propTypes = {};

export { ModalResendEmail };
